import React, {
    useRef,
    useMemo,
    useState,
    useEffect,
    useContext,
    useCallback
} from "react";
import { DirectionalHint, IconButton, TooltipHost  } from "@fluentui/react";
import { TbTextScan2 } from "react-icons/tb";
import { pdfjs, Page, Document } from "react-pdf";
import type { PDFDocumentProxy } from "pdfjs-dist";

import debounce from "lodash/debounce";
import stylesXplrr from "./XplrrBOXPDFZoom.module.css";
import { ProgressIndicator } from "@fluentui/react/lib/ProgressIndicator";
import { GlobalContext } from "../../GlobalContext";
import {
    gfile,
    gpdftextitem,
    gpagedimensions,
    gautoparagraphresult,
    get_global_file_cache_empty
} from "../../interfaces";
import {
    findRange,
    get_mod_str,
    contentPath,
    pdfCacheKey,
    getDesiredWidth,
    areAllFlagsTrue,
    getDesiredHeight,
    storeRescaleWidth,
    saveEntriesToGlobal
} from "../../util_glob";
import { tooltipZoomMngPdfStyles, zoomInOutViewer, tooltipZoomMngPdfDedicatedStyles } from "../../styles_glob";
import { ParagraphOverlay } from "../ParagraphOverlayFX/ParagraphOverlay";
import { pdfsrccodestype, emptyPdfBase64 } from "../../lsts";
import { qGetFileParagraps } from "../../api/api";
import { saveItem, loadItem, removeItem } from "../../storage";
import ParagraphOverlayPulseButton from "../ParagraphOverlayPulseButton/ParagraphOverlayPulseButton";
import { ImFilesEmpty } from "react-icons/im";
import { MdErrorOutline } from "react-icons/md";
import { FiZoomIn, FiZoomOut } from "react-icons/fi";
import { TbArrowAutofitWidth, TbBoxAlignLeft, TbCapture } from "react-icons/tb";
import { captureCanvasCrop, RectCropData } from "./pdfCropUtil";


pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

(pdfjs.GlobalWorkerOptions as any).useSystemFonts = true;
(pdfjs.GlobalWorkerOptions as any).disableFontFace = false;
(pdfjs.GlobalWorkerOptions as any).fontExtraProperties = true;

let timeoutId: number;

interface PDFBoxDetails {
    file: gfile | undefined;
    handleTimeout: () => void;
    handleScroll: (pageNumCur: number) => void;
    setPages: (numPages: number) => void;
    setContentList: (contentList: { title: string; dest: number }[]) => void;
    startPage: number;
    activeFileId: string;
    callBackVisibleEntries: (vis: number[]) => void;
    callBackIsLoading: (isL: boolean) => void;
    srcLoc: pdfsrccodestype;
    companyId: string;
    panelWidthRight?: number;
    resizeTriggerBack: (width: number) => void;
    isResizing: boolean;
}
interface PDFPageDetails {
    handleTimeout: () => void;
    setContentList: (contentList: { title: string; dest: number }[]) => void;
    nums: number;
    startPage: number;
    fileId: string;
    fileIndexChunks: number[];
    filePageDimsState: gpagedimensions[] | [];
    rescaleWidthParam?: number;
}

const XplrrBOXPDFZoom = ({
    file,
    handleTimeout,
    handleScroll,
    setPages,
    setContentList,
    startPage,
    activeFileId,
    callBackVisibleEntries,
    callBackIsLoading,
    srcLoc,
    companyId,
    panelWidthRight,
    resizeTriggerBack,
    isResizing
}: PDFBoxDetails) => {
    const { fileList, loggedInUser, selectedGroup } = useContext(GlobalContext);
    const [pgs, setPgs] = useState<React.ReactElement<any, any>[]>([]);                                                             // pdf viewer ready page htmls and page placeholders
    const [isLoa, setIsLoa] = useState<boolean>(true);                                                                              // loading flag ⇒ false on file binary here whole (via download or cache)
    const [fileId, setFileId] = useState<string>(activeFileId);                                                                     // current file id
    const [lensPos, setLensPos] = useState<{ x: number; y: number; page: number } | null>(null);                                    // zoom rectangle (legacy)
    const [scrollTr, setScrollTr] = useState<string>("");                                                                           // date string for scroll refresh tracking (legacy)
    const [progress, setProgress] = useState<number>();                                                                             // download progress ⇒ 1 is max
    const [loadMark, setLoadMark] = useState<string>();                                                                             // string for div key to trigger rerender
    const [numPages, setNumPages] = useState<number>(0);                                                                            // total number of pages in doc (got on load from PDFDocument)
    const [zoomActive, setZoomActive] = useState<boolean>(false);
    const [lensFrozen, setLensFrozen] = useState<boolean>(false);                                                                   // track if lens is frozen (legacy)
    const [resizeCount, setResizeCount] = useState<number>(0);                                                                      // rerender trigger on manual browser resize
    const [hoveredPage, setHoveredPage] = useState<number | null>(null);                                                            // zoom rectangle (legacy)
    const [rescaleWidth, setRescaleWidth] = useState<number>(getDesiredWidth(srcLoc, selectedGroup.selectionId, true));             // main page width prop
    const [isBlobPulling, setIsBlobPulling] = useState<boolean>(false);                                                             // downloading doc (from server - not cache)
    const [loadingMessage, setLoadingMessage] = useState<string>(activeFileId);
    const [fileIndexChunks, setFileIndexChunks] = useState<number[]>([]);
    const [onDemandRefresh, setOnDemandRefresh] = useState<number>(0);                                                              // refresh trigger (check & refactor)
    const [filePageDimsState, setFilePageDimsState] = useState<gpagedimensions[] | []>([]);
    const [manualResizeWidth, setManualResizeWidth] = useState<number>(getDesiredWidth(srcLoc, selectedGroup.selectionId, true));   // resize by zoom buttons
    const [listenForZoomActive, setListenForZoomActive] = useState<boolean>(false);                                                 // zoom rectangle (legacy)
    const [rectSelectMode, setRectSelectMode] = useState<boolean>(false);                                                            // rectangle text select mode
    const [copiedToast, setCopiedToast] = useState<string>('');                                                                  // clipboard toast
    const [rectCropData, setRectCropData] = useState<RectCropData | null>(null);                                                // cropped image from rect select
    const [showParagraphOverlay, setShowParagraphOverlay] = useState<boolean>(false);
    const [isParagraphDataLoaded, setIsParagraphDataLoaded] = useState<boolean>(false);
    const [paragraphDataLoadedForId, setParagraphDataLoadedForId] = useState<string>("0");
    const pageRefs = useRef<(Element | null)[]>([]);
    const observerRef = useRef<IntersectionObserver | null>(null);
    const pageSizeRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
    const containerRef = useRef(null);
    const paragraphsRef = useRef([]);
    const prevIsResizingRef = useRef(isResizing);
    const rectDragRef = useRef<{ page: number; pageDiv: HTMLElement; startClientX: number; startClientY: number; endClientX: number; endClientY: number } | null>(null);
    const rectOverlayRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Shift") {
                if (listenForZoomActive == true) return;
                setListenForZoomActive(true);
            }
        };
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === "Shift") setListenForZoomActive(false);
        };
        const handleResize = debounce(() => {
            setResizeCount(prevCount => prevCount + 1);
            setRescaleWidth(getDesiredWidth(srcLoc, selectedGroup.selectionId));
        }, 100);

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("keyup", handleKeyUp);
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
            window.removeEventListener("keyup", handleKeyUp);
            window.removeEventListener("resize", handleResize);
            handleResize.cancel();
        };
    }, []);

    useEffect(() => {
        import.meta.env.DEV && console.log("‹show_paragraph_overlay›");
        if (!fileId) return;

        window.paragraphOverlayOn = showParagraphOverlay;

        if (showParagraphOverlay == true){
            if (paragraphDataLoadedForId === fileId && paragraphsRef.current.length > 0) {
                setIsParagraphDataLoaded(true);
                return;
            }
            qGetFileParagraps(fileId, loggedInUser).then((res: gautoparagraphresult[]) => {
                paragraphsRef.current = res as any;
                setParagraphDataLoadedForId(fileId);
                setIsParagraphDataLoaded(true);
            });
        }
    }, [showParagraphOverlay]);

    useEffect(() => {
        window.paragraphOverlayOn = showParagraphOverlay;

        if (!fileId) return;
        paragraphDataLoadedForId !== fileId && setIsParagraphDataLoaded(false);
    }, [fileId]);


    useEffect(() => {
        if (!panelWidthRight) panelWidthRight = 0;
        if (isResizing) {
            // this is ref, need to not exist to trigger resize callback
        } else {
            resizeTriggerBack(panelWidthRight);
        }
    }, [resizeCount, panelWidthRight]);

    useEffect(() => {
        if (prevIsResizingRef.current && !isResizing) resizeTriggerBack(panelWidthRight || 0);

        setResizeCount(prevCount => prevCount + 1);

        prevIsResizingRef.current = isResizing;

    }, [isResizing]);

    useEffect(() => {
        if (!activeFileId) return;
        let visibleEntries: number[] = [];
        let cacheEntries: number[] = [];
        observerRef.current = new IntersectionObserver(
            entries => {
                let currentEntries: number[] = [];
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const pageNumber = parseInt((entry.target as HTMLElement).dataset.pageNumber || "1", 10);
                        if (!visibleEntries.includes(pageNumber)) visibleEntries.push(pageNumber);
                        currentEntries.push(pageNumber);
                    }
                });
                visibleEntries = visibleEntries.sort((a, b) => a - b);
                if (currentEntries.length < 1) currentEntries = cacheEntries;
                visibleEntries = currentEntries;
                const minVisible = Math.min(...visibleEntries);
                const maxVisible = Math.max(...visibleEntries);
                for (let i = 1; i <= 5; i++) {
                    visibleEntries.push(maxVisible + i);
                    visibleEntries.push(minVisible - i);
                }
                if (currentEntries.length > 0) cacheEntries = currentEntries;
                callBackVisibleEntries(visibleEntries);
                saveEntriesToGlobal(currentEntries);
            },
            { root: containerRef.current, rootMargin: "1000px 0px" }
        );

        pageRefs.current.forEach(ref => {
            if (ref) observerRef?.current?.observe(ref);
        });
    }, [activeFileId, resizeCount]);

    useEffect(() => {
        callBackIsLoading(true);

        setLoadMark(`${file ? "fileY" : "fileN"}_${activeFileId}_${startPage}_${window.globalBase64String?.length}`);

        if (!file) return;
        if (file.indexchunks && startPage) {
            setFileIndexChunks(findRange(file.indexchunks, startPage));
        } else {
            setFileIndexChunks([startPage - 1]);
        }

        // 3 level cache system - global_var, brwsr_db, srv_io. tracks state ⇒ check content, check in-progress global_var, check db and then dw
        const cacheBase64Wndw = async (fl: gfile): Promise<boolean> => {
            if (!fl || !fl.fileid || !fl.modified) return false;

            setIsBlobPulling(true);

            if (window.globalPDFCache === undefined) window.globalPDFCache = get_global_file_cache_empty();
            if (window.globalPreLoadMap === undefined) window.globalPreLoadMap = new Map<string, string>();

            const cacheKey = pdfCacheKey(fl.fileid, fl.modified);
            const flPath = contentPath(fl.name, fl.fileid);

            if (!window.globalPDFCache.has(cacheKey) && window.globalPreLoadMap.has(cacheKey)) return false // file loading right now (no cache key, and not in premap)


            if (window.globalPDFCache.has(cacheKey)) {
                setLoadingMessage(`CONTENT FETCH ${Math.round(parseInt(fl.size) / (1024 * 1024))}MB`);

                const cacheVal = window.globalPDFCache.get(cacheKey)?.base64string;

                if (!cacheVal || cacheVal == "" || cacheVal == undefined) {
                    handleNoValidCacheVal(cacheKey, fl);
                    return false;
                }

                let pgDms = window.globalPDFCache.get(cacheKey)?.pageDimensions;

                if (!pgDms || pgDms.length == 0) pgDms = fl.pagedimensions;

                window.globalPDFPageDimensions = pgDms.map((dim, index) => ({ ...dim, scaledHeight: getDesiredHeight(dim, srcLoc, selectedGroup.selectionId) }));
                window.globalBase64String = `${emptyPdfBase64}${cacheVal}`;

                setOnDemandRefresh(prevNum => prevNum + 1);
                window.globalBase64StringId = cacheKey;

                setProgress(1);
                return true;

            } else {
                let dCache = await loadItem(cacheKey); // get from browser db

                if (dCache) {
                    process.env.NODE_ENV === "development" && console.log("‹d_cache_hit›");

                    const cacheVal = dCache?.base64string;
                    if (!cacheVal || cacheVal == "" || cacheVal == undefined) {
                        handleNoValidCacheVal(cacheKey, fl);
                        return false;
                    }
                    let pgDms = dCache?.pageDimensions;
                    if (!pgDms || pgDms.length == 0) pgDms = fl.pagedimensions;
                    window.globalPDFPageDimensions = (pgDms as gpagedimensions[]).map((dim, index) => ({
                        ...dim,
                        scaledHeight: getDesiredHeight(dim, srcLoc, selectedGroup.selectionId)
                    }));
                    window.globalBase64String = `${emptyPdfBase64}${cacheVal}`;
                    setOnDemandRefresh(prevNum => prevNum + 1);
                    window.globalBase64StringId = cacheKey;
                    setProgress(1);
                    window.globalPDFCache.set(cacheKey, dCache);
                    return true;
                }

                window.globalPreLoadMap.set(cacheKey, cacheKey);
                const params = new URLSearchParams({
                    cachemodstring: get_mod_str(fl.modified)
                });
                let flSz = Math.round(parseInt(fl.size) / (1024 * 1024));
                setLoadingMessage(`INITIAL LOAD ${flSz}MB ${flSz > 5 ? "PLEASE WAIT..." : ""}`);

                const base64 = await downloadFile(`/content/${companyId}/${flPath}` + `?${params.toString()}`);
                if (!base64 || base64 == "" || base64 == undefined) {
                    window.globalPDFCache.delete(cacheKey);
                    window.globalPreLoadMap.delete(cacheKey);
                    removeItem(cacheKey);
                    return false;
                }
                if (fl?.pagedimensions) {
                    window.globalPDFPageDimensions = fl.pagedimensions.map((dim, index) => ({ ...dim, scaledHeight: getDesiredHeight(dim, srcLoc, selectedGroup.selectionId) }));
                } else {
                    window.globalPDFPageDimensions = fl.pagedimensions ?? [];
                }

                window.globalBase64String = `${emptyPdfBase64}${base64}`;
                window.globalBase64StringId = cacheKey;
                window.globalPDFCache.set(cacheKey, {
                    base64string: `${base64}`,
                    fileid: fl.fileid,
                    name: fl.name,
                    modified: fl.modified,
                    pageContent: fl.pagecontent,
                    pageDimensions: fl.pagedimensions
                });
                saveItem(cacheKey, {
                    base64string: `${base64}`,
                    fileid: fl.fileid,
                    name: fl.name,
                    modified: fl.modified,
                    pageContent: fl.pagecontent,
                    pageDimensions: fl.pagedimensions
                });
                setIsBlobPulling(false);
                setIsLoa(false);
                setProgress(1);
                setLoadMark(`${file ? "fileY" : "fileN"}_${activeFileId}_${startPage}_${window.globalBase64String?.length}`);
                setOnDemandRefresh(prevNum => prevNum + 1);
                return true;
            }
        };
        const handleNoValidCacheVal = (cacheKey: string, fl: gfile) => {
            removeItem(cacheKey);
            window.globalPDFCache.delete(cacheKey);
            window.globalPreLoadMap.delete(cacheKey);
            try {
                console.log("errr_cache_retry");
                cacheBase64Wndw(fl);
            } catch (e) {
                console.log("errr_cachebase", e);
            }
        };
        if (activeFileId != null && activeFileId != "" && activeFileId != undefined) {
            cacheBase64Wndw(file).then((opResult: boolean) => {
                if (opResult == true) {
                    setFileId(activeFileId);
                    setIsLoa(false);
                }
                callBackIsLoading(false);
            });
        } else {
            callBackIsLoading(false);
        }
    }, [activeFileId]);

    useEffect(() => {
        setOnDemandRefresh(prevNum => prevNum + 1);
    }, [resizeCount]);

    const addToObserver = useCallback((ref: any, index: number) => {
        if (ref) {
            pageRefs.current[index] = ref;
            ref.dataset.pageNumber = (index + 1).toString();
            if (observerRef.current) observerRef.current.observe(ref);
        }
    }, []);

    const pdfOptions = useMemo(
        () => ({
            cMapUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/cmaps/",
            cMapPacked: true,
            standardFontDataUrl: "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/standard_fonts/",
            fontExtraProperties: true,
            useSystemFonts: true
        }),
        []
    );

    const ifBase64NotReady = useMemo(
        () => (!window.globalBase64String || window.globalBase64String == emptyPdfBase64 || window.globalBase64String == ""),
        [window.globalBase64String]
    );
    const downloadFile = async (url: string): Promise<string | null> => {
        try {
            setProgress(0.01);
            const response = await fetch(url);
            if (response && response.body) {
                const reader = response.body.getReader();
                const contentLengthHeader = response.headers.get("kgnfcl");
                const contentLength = contentLengthHeader ? parseInt(contentLengthHeader, 10) : 1;
                let receivedLength = 0;
                const chunks: Uint8Array[] = [];
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    chunks.push(value);
                    receivedLength += value.length;
                    setProgress(receivedLength / contentLength);
                    setLoadingMessage(prevMessage => prevMessage.replace("INITIAL LOAD", "DOWNLOADING ON DEVICE"));
                }
                const allChunks = new Uint8Array(receivedLength);
                let position = 0;
                for (const chunk of chunks) {
                    allChunks.set(chunk, position);
                    position += chunk.length;
                }
                const chunkSize = 0x8000; // 32kb chunks
                let base64 = '';
                for (let i = 0; i < allChunks.length; i += chunkSize) {
                    const chunk = allChunks.subarray(i, i + chunkSize);
                    base64 += String.fromCharCode.apply(null, chunk as unknown as number[]);
                }
                return btoa(base64);
            }
        } catch (err) {
            console.error("‹download_failed›", err);
        }
        return null;
    };

    const handleLoadSuccess = async (pdfObject: PDFDocumentProxy) => {
        const numPages = pdfObject.numPages;
        window.globalNumPages = numPages;
        const pgs = preparePdfPageCallBack({
            handleTimeout: handleTimeout,
            setContentList: setContentList,
            startPage: startPage,
            nums: numPages,
            fileId: fileId,
            fileIndexChunks: fileIndexChunks,
            filePageDimsState: filePageDimsState
        });
        setPgs(pgs);
        setFilePageDimsState(window.globalPDFPageDimensions?.map((dim, index) => ({ ...dim, scaledHeight: getDesiredHeight(dim, srcLoc, selectedGroup.selectionId) })));
        setOnDemandRefresh(prevNum => prevNum + 1);
        setTimeout(() => { setOnDemandRefresh(prevNum => prevNum + 1)}, 100);
        setNumPages(numPages);
        setPages(numPages);
    };

    const preparePdfPageCallBack = useCallback(({ handleTimeout, nums, setContentList, startPage, fileId, fileIndexChunks, filePageDimsState, rescaleWidthParam }: PDFPageDetails) => {
        const pages = [];
        window.globalItemCounts = new Map<number, { count: number; flag: boolean }>();

        let resizeWidthDynamic = rescaleWidth;
        if (rescaleWidthParam && rescaleWidthParam > 0) resizeWidthDynamic = rescaleWidthParam;

        for (let i = 0; i < nums; i++) {
            pages.push(
                <Page
                    key={`pg_${i + 1}`}
                    pageNumber={i + 1}
                    style={{ overflowY: true, behavior: "smooth", border: "1px solid black" }}
                    width={resizeWidthDynamic}
                    loadingText={<div className={stylesXplrr.loadingTextMain}>LDNG</div>}
                    noData={<div className={stylesXplrr.loadingTextMain}>LDNG</div>}
                    onRenderTextLayerError={(error: Error) => {}}
                    onGetTextSuccess={(items: any, styles: any) => {
                        // prepare page item counts for flagging
                        if (!window.globalItemCounts) window.globalItemCounts = new Map<number, { count: number; flag: boolean }>();
                        window.globalItemCounts.set(i, { count: items.items.length, flag: false });
                    }}
                    customTextRenderer={({ pageIndex, pageNumber, itemIndex, str }: gpdftextitem) => {
                        try {
                            // if item is last on page set flag to true
                            if (itemIndex == (window.globalItemCounts.get(i)?.count || 0) - 1) {
                                window.globalItemCounts.set(i, {
                                    count: window.globalItemCounts.get(i)?.count || 0,
                                    flag: true
                                });
                                // if all page page ready flags are true, set loaded to true
                                if (areAllFlagsTrue(window.globalItemCounts, fileIndexChunks)) {
                                    if (window.globalAllNessesaryPDFPagesLoaded == false) {
                                        window.globalAllNessesaryPDFPagesLoaded = true;
                                        try {
                                            clearTimeout(timeoutId); // clear previous timeout
                                        } catch {
                                            import.meta.env.DEV && console.log("‹no_timeout_to_clear›");
                                        }
                                        timeoutId = setTimeout(handleTimeout, 100) as unknown as number;
                                    }
                                }
                            }
                        } catch (e) {
                            import.meta.env.DEV && console.log("‹error‾in‾customTextRenderer›", e);
                        }
                        return `<span id='CSP_${pageIndex}_${itemIndex}' class='kjgi pdf_itm_${pageIndex}'>${str}</span>`;
                    }}
                />
            );
        }
        return pages;

    }, [manualResizeWidth]);

    const getCurrentPage = (pageDimensions: gpagedimensions[], scrollTop: number, clientHeight: number): number => {
        let cumulativeHeight = 0;
        const middleOfViewport = scrollTop + clientHeight / 2;
        for (let i = 0; i < pageDimensions.length; i++) {
            cumulativeHeight += getDesiredHeight(pageDimensions[i], srcLoc, selectedGroup.selectionId, rescaleWidth);
            if (cumulativeHeight > middleOfViewport) return i + 1; // +1 because page numbers typically start at 1, not 0
        }
        return pageDimensions.length; // last page
    };

    const handleScrollCallAdv = (event: any) => {
        let { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
        handleScroll(getCurrentPage(filePageDimsState, scrollTop, clientHeight));
        debounce(() => {setScrollTr(new Date().toISOString())}, 50)();
    };

    const handlePdfClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, pageIdx: number) => {
        console.log("‹pdf_click›", "pfg");
        if (e.shiftKey || zoomActive || listenForZoomActive) {
            if (!zoomActive) {
                // first click: activate lens, not frozen, set position
                setZoomActive(true);
                setLensFrozen(false);
                const pageDiv = e.currentTarget as HTMLElement;
                const rect = pageDiv.getBoundingClientRect();
                const xRaw = e.clientX - rect.left;
                const yRaw = e.clientY - rect.top;
                setLensPos({ x: xRaw, y: yRaw, page: pageIdx });
                setHoveredPage(pageIdx);
            } else if (!lensFrozen) {
                // second click: freeze lens in place
                setLensFrozen(true);
            } else {
                // third click: remove lens (optional, or keep frozen)
                setZoomActive(false);
                setLensFrozen(false);
                setLensPos(null);
                setHoveredPage(null);
            }
        }
        e.preventDefault(); // prevent text selection
        e.stopPropagation();
    };

    const handleMouseMove = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, pageIdx: number) => {
        if (!zoomActive || lensFrozen) return; // only move lens if not frozen
        e.preventDefault(); // prevent text selection
        e.stopPropagation();
        const pageDiv = e.currentTarget as HTMLElement;
        const rect = pageDiv.getBoundingClientRect();
        const xRaw = e.clientX - rect.left;
        const yRaw = e.clientY - rect.top;
        setLensPos({ x: xRaw, y: yRaw, page: pageIdx });
        setHoveredPage(pageIdx);
    };
    // rectangle text selection handler
    const handleRectMouseDown = (e: React.MouseEvent, pageIdx: number) => {
        if (!rectSelectMode) return;
        const pageDiv = e.currentTarget as HTMLElement;
        rectDragRef.current = {
            page: pageIdx,
            pageDiv: pageDiv,
            startClientX: e.clientX,
            startClientY: e.clientY,
            endClientX: e.clientX,
            endClientY: e.clientY
        };
        if (rectOverlayRef.current) {
            rectOverlayRef.current.style.display = "block";
            rectOverlayRef.current.style.left = `${e.clientX}px`;
            rectOverlayRef.current.style.top = `${e.clientY}px`;
            rectOverlayRef.current.style.width = "0px";
            rectOverlayRef.current.style.height = "0px";
        }
        const handleDocMouseMove = (ev: MouseEvent) => {
            if (!rectDragRef.current) return;
            const drag = rectDragRef.current;
            drag.endClientX = ev.clientX;
            drag.endClientY = ev.clientY;
            if (rectOverlayRef.current) {
                rectOverlayRef.current.style.left = `${Math.min(drag.startClientX, ev.clientX)}px`;
                rectOverlayRef.current.style.top = `${Math.min(drag.startClientY, ev.clientY)}px`;
                rectOverlayRef.current.style.width = `${Math.abs(ev.clientX - drag.startClientX)}px`;
                rectOverlayRef.current.style.height = `${Math.abs(ev.clientY - drag.startClientY)}px`;
            }
            ev.preventDefault();
        };
        const handleDocMouseUp = (ev: MouseEvent) => {
            console.log("‹hdm›");
            document.removeEventListener("mousemove", handleDocMouseMove);
            document.removeEventListener("mouseup", handleDocMouseUp);
            if (!rectDragRef.current) return;
            const drag = rectDragRef.current;
            if (rectOverlayRef.current) rectOverlayRef.current.style.display = "none";
            const pageDivRect  = drag.pageDiv.getBoundingClientRect();
            const selLeft      = Math.min(drag.startClientX, ev.clientX) - pageDivRect.left;
            const selTop       = Math.min(drag.startClientY, ev.clientY) - pageDivRect.top;
            const selRight     = Math.max(drag.startClientX, ev.clientX) - pageDivRect.left;
            const selBottom    = Math.max(drag.startClientY, ev.clientY) - pageDivRect.top;
            if (Math.abs(selRight - selLeft) < 5 || Math.abs(selBottom - selTop) < 5) {
                rectDragRef.current = null;
                return;
            }
            const textSpans = drag.pageDiv.querySelectorAll(`.pdf_itm_${drag.page}`);
            const selectedTexts: { top: number; left: number; text: string }[] = [];
            textSpans.forEach(span => {
                const spanRect = span.getBoundingClientRect();
                const spanLeft = spanRect.left - pageDivRect.left;
                const spanTop = spanRect.top - pageDivRect.top;
                const spanRight = spanRect.right - pageDivRect.left;
                const spanBottom = spanRect.bottom - pageDivRect.top;
                if (spanRight > selLeft && spanLeft < selRight && spanBottom > selTop && spanTop < selBottom) {
                    selectedTexts.push({ top: spanTop, left: spanLeft, text: span.textContent || "" });
                }
            });
            selectedTexts.sort((a, b) => {
                if (Math.abs(a.top - b.top) < 5) return a.left - b.left;
                return a.top - b.top;
            });
            let text = selectedTexts.filter(s => s.text.trim()).map(s => s.text).join(`\n`);
            if (text.trim()) {
                navigator.clipboard.writeText(text.trim()).then(() => {
                    setCopiedToast(text.trim().length > 100 ? text.trim().slice(0, 100) + "..." : text.trim());
                    setTimeout(() => setCopiedToast(''), 2000);
                });
            }
            const croppedBase64 = captureCanvasCrop(drag.pageDiv, selLeft, selTop, selRight, selBottom);

            if (croppedBase64) {
                setRectCropData({ base64: croppedBase64, page: drag.page + 1, selectedText: text.trim() });
                console.log(`‹rect_crop› page=${drag.page + 1} size=${Math.round(croppedBase64.length / 1024)}KB`);
            }
            rectDragRef.current = null;
            ev.preventDefault();

        };
        document.addEventListener("mousemove", handleDocMouseMove);
        document.addEventListener("mouseup", handleDocMouseUp);
        e.preventDefault();
        e.stopPropagation();
    };

    const renderedPageWidthsVar = useMemo(() => {
        let resizeWidthDynamic = rescaleWidth;
        if(manualResizeWidth > 0) resizeWidthDynamic = manualResizeWidth;
        return filePageDimsState?.map((dim: gpagedimensions, index: number) => {
            return {
                width: resizeWidthDynamic,
                height: getDesiredHeight(dim, srcLoc, selectedGroup.selectionId, resizeWidthDynamic)
            }
        });
    }, [manualResizeWidth, filePageDimsState, window.innerWidth]);

    const renderedPages = useMemo(() => {
        if (!filePageDimsState || filePageDimsState.length == 0) return null;
        if (ifBase64NotReady) return null;

        return filePageDimsState?.map((dim: gpagedimensions, index: number) => {
            return (
                (index > startPage - (window.globalpdfObservableRange || 5) && index < startPage + (window.globalpdfObservableRange || 5)) ||
                (window.globalUserScrollLazyLoadEnabled === true && window.globalCurrentVisibleEntries?.includes(index))
            ) ? (
                    <div className={stylesXplrr.pdfPageWrap} key={`${index}_wrp`}>
                        <div
                            key={`${index}_page`}
                            data-load-mark={loadMark}
                            data-p-type={"page"}
                            ref={(ref: any) => {
                                addToObserver(ref, index);
                                pageSizeRefs.current[index] = ref;
                            }}
                            className={`${stylesXplrr.pdfPageInvSubWrap}${rectSelectMode ? " " + stylesXplrr.rectSelectCursor : ""}`}
                            style={{
                                width: renderedPageWidthsVar?.[index]?.width,
                                height: renderedPageWidthsVar?.[index]?.height
                            }}
                            onClick={e => { if (rectSelectMode) { e.preventDefault(); return; } handlePdfClick(e, index); }}
                            onMouseDown={e => handleRectMouseDown(e, index)}
                            onMouseMove={e => { if (rectSelectMode) return; handleMouseMove(e, index); }}
                        >
                            {pgs[index]}
                        </div>
                        {/* ANCHOR[id=ParagraphOverlay] Paragraph Overlay */}
                        {(isParagraphDataLoaded && showParagraphOverlay) && (
                            <ParagraphOverlay
                                paragraphs={paragraphsRef.current}
                                pagedimensions={filePageDimsState}
                                pageNumber={index}
                                pageWidth={renderedPageWidthsVar?.[index]?.width || 0}
                                pageHeight={renderedPageWidthsVar?.[index]?.height || 0}
                                visible={showParagraphOverlay}
                                onRectCrop={(cropData) => setRectCropData(cropData)}
                            />
                        )}
                    </div>
                ) : (
                    <div
                        key={`${index}_mock`}
                        data-load-mark={loadMark}
                        data-p-type={"mock"}
                        ref={(ref: any) => addToObserver(ref, index)}
                        className={stylesXplrr.pdfPageInvSubWrap}
                        style={{
                            width: renderedPageWidthsVar?.[index]?.width,
                            height: renderedPageWidthsVar?.[index]?.height,
                        }}
                    />
            )
        });
    }, [
        filePageDimsState,
        startPage,
        window.globalpdfObservableRange,
        window.globalUserScrollLazyLoadEnabled,
        window.globalCurrentVisibleEntries,
        rescaleWidth,
        addToObserver,
        getDesiredHeight,
        loadMark,
        pgs,
        srcLoc,
        window.globalWigglePixel,
        window.globalHiglightFound,
        window.global_wiggle_stamp,
        rectSelectMode,
        isParagraphDataLoaded,
        showParagraphOverlay
    ]);

    return (
        <div id="pdf-viewer" className={stylesXplrr.pdfViewer}>
            {/* zoom buttons */}
            <div style={{ position: 'absolute', top: '-1px',  display: 'flex', alignItems: 'center', width: `${panelWidthRight}px`, justifyContent: 'flex-end' }}>

                {/* set pdf to 100% panel width */}
                <IconButton
                    className={stylesXplrr.zoomInButton}
                    styles={zoomInOutViewer}
                    onClick={(e) => {
                        // method 1: get the immediate parent's width
                        const button = e.currentTarget as HTMLElement;
                        const parent = button.parentElement?.parentElement;
                        if (parent) {
                            const parentWidth = parent.offsetWidth; // or parent.clientWidth for inner width without borders

                            // set the manual resize width to fit the parent container
                            setManualResizeWidth(parentWidth);
                            setRescaleWidth(parentWidth);
                            storeRescaleWidth(selectedGroup.selectionId, parentWidth);

                            // update the pdf pages with new width
                            const pgs = preparePdfPageCallBack({
                                handleTimeout: handleTimeout,
                                setContentList: setContentList,
                                startPage: startPage,
                                nums: numPages,
                                fileId: fileId,
                                fileIndexChunks: fileIndexChunks,
                                filePageDimsState: filePageDimsState,
                                rescaleWidthParam: parentWidth
                            });
                            setPgs(pgs);
                        }
                    }}
                    >
                    <TooltipHost styles={tooltipZoomMngPdfStyles} content="Expand Document to Full Panel Width" className={stylesXplrr.tooltipZoomMng}>
                        <TbArrowAutofitWidth size={20}/>
                    </TooltipHost>
                </IconButton>

                {/* set pdf to 50% panel width */}
                <IconButton
                    className={stylesXplrr.zoomInButton}
                    styles={zoomInOutViewer}
                    onClick={(e) => {
                        // method 1: get the immediate parent's width
                        const button = e.currentTarget as HTMLElement;
                        const parent = button.parentElement?.parentElement;
                        if (parent) {
                            const parentWidth = parent.offsetWidth / 2; // or parent.clientWidth for inner width without borders

                            setRescaleWidth(parentWidth);
                            setManualResizeWidth(parentWidth);
                            setRescaleWidth(parentWidth);
                            storeRescaleWidth(selectedGroup.selectionId, parentWidth);

                            // update the pdf pages with new width
                            const pgs = preparePdfPageCallBack({
                                handleTimeout: handleTimeout,
                                setContentList: setContentList,
                                startPage: startPage,
                                nums: numPages,
                                fileId: fileId,
                                fileIndexChunks: fileIndexChunks,
                                filePageDimsState: filePageDimsState,
                                rescaleWidthParam: parentWidth
                            });
                            setPgs(pgs);
                        }
                    }}
                    >
                    <TooltipHost styles={tooltipZoomMngPdfStyles} content="Resize Document to half the Panel Width" className={stylesXplrr.tooltipZoomMng}>
                        <TbBoxAlignLeft size={20}/>
                    </TooltipHost>
                </IconButton>

                {/* zoom in pdf */}
                <IconButton
                    className={stylesXplrr.zoomInButton}
                    styles={zoomInOutViewer}
                    onClick={() => {
                        setManualResizeWidth(manualResizeWidth + 100);
                        setRescaleWidth(prev => prev + 100);
                        storeRescaleWidth(selectedGroup.selectionId, manualResizeWidth + 100);
                        const pgs = preparePdfPageCallBack({
                            handleTimeout: handleTimeout,
                            setContentList: setContentList,
                            startPage: startPage,
                            nums: numPages,
                            fileId: fileId,
                            fileIndexChunks: fileIndexChunks,
                            filePageDimsState: filePageDimsState,
                            rescaleWidthParam: manualResizeWidth + 100
                        });
                        setPgs(pgs);
                    }}
                >
                    <FiZoomIn size={20}/>
                </IconButton>

                {/* zoom out pdf */}
                <IconButton
                    className={stylesXplrr.zoomOutButton}
                    styles={zoomInOutViewer}
                    onClick={() => {
                        setManualResizeWidth(manualResizeWidth - 100);
                        setRescaleWidth(prev => prev - 100);
                        storeRescaleWidth(selectedGroup.selectionId, manualResizeWidth - 100);
                        const pgs = preparePdfPageCallBack({
                            handleTimeout: handleTimeout,
                            setContentList: setContentList,
                            startPage: startPage,
                            nums: numPages,
                            fileId: fileId,
                            fileIndexChunks: fileIndexChunks,
                            filePageDimsState: filePageDimsState,
                            rescaleWidthParam: manualResizeWidth - 100
                        });
                        setPgs(pgs);
                    }}
                    >
                    <FiZoomOut size={20} />
                </IconButton>
                {srcLoc != "sbs" && (
                    <TooltipHost directionalHint={DirectionalHint.bottomCenter}
                        styles={tooltipZoomMngPdfDedicatedStyles}
                        content={
                            showParagraphOverlay ? (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', borderLeft: '4px solid #ffcc00', paddingLeft: 4, gap: 4, fontFamily: "Raleway, Urbanist, Roboto, sans-serif"  }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', paddingLeft: 4, fontSize: 12, fontWeight: 600, color: '#333' }}>
                                        Paragraph Overlay Enabled & Drag-to-Select Enabled
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', marginTop: 4, fontSize: 10, color: '#666' }}>
                                        Each Text element lights up on mousehover and can be copied with click. Drag a square to select multiple elements. They are auto-copied on mouse left-button release.
                                    </div>
                                </div>
                            )
                            : (
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', borderLeft: '4px solid #ffcc00', paddingLeft: 4, gap: 6, fontFamily: "Raleway, Urbanist, Roboto, sans-serif"  }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', paddingLeft: 4, fontSize: 12, fontWeight: 600, color: '#333' }}>
                                        Show Paragraph Overlay & Enable Drag-to-Select
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start', paddingLeft: 4, marginTop: 4, fontSize: 10, color: '#666' }}>
                                        Each Text element lights up on mousehover and can be copied with click. Drag a square to select multiple elements. They are auto-copied on mouse left-button release.
                                    </div>
                                </div>
                            )
                        }
                    >
                        <ParagraphOverlayPulseButton
                            showParagraphOverlay={showParagraphOverlay}
                            setShowParagraphOverlay={setShowParagraphOverlay}
                            zoomInOutViewer={zoomInOutViewer}
                            buttonClassName={`${stylesXplrr.zoomOutButton} ${showParagraphOverlay ? stylesXplrr.showParagraphOverlay : ''}`}
                        />
                    </TooltipHost>
                )}
                {/* rectangle text select */}


                {selectedGroup.enabledocaudio == 1  && (
                    <div className={stylesXplrr.mockAudioContainer}>
                    </div>
                )}
                <div className={stylesXplrr.mockCloseButtonInv}>
                </div>
            </div>

            {fileList.length == 0 ? (
                <div className={stylesXplrr.noDocs}>
                    <div className={stylesXplrr.iconNoContent}>
                        <ImFilesEmpty size={24} />
                    </div>
                    <div>Space Empty. Upload documents from the Management Tab</div>
                </div>
            ) : (progress || 0) < 1 ? (
                <div className={stylesXplrr.loadIndiWrap}>
                    <div key={rescaleWidth} style={{width: rescaleWidth / 2}} className={stylesXplrr.loadIndi}>
                        <ProgressIndicator percentComplete={progress} barHeight={20} progressHidden={(progress || 0) >= 1} />
                    </div>
                    <div className={stylesXplrr.loadingInfoContainer}>
                        <div className={stylesXplrr.loadingTextInvZ}>
                            {loadingMessage}
                            {loadingMessage == "ERROR LOADING PDF" ? null : (
                                <div className={stylesXplrr.typingDotsInv}>
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            ) : null}
            {ifBase64NotReady || !filePageDimsState ?
                null :  (
                <div key={`pdfz_${progress}`} id={fileId} className={stylesXplrr.pdfViewerSub} ref={containerRef}>
                    <Document
                        className={stylesXplrr.pdfViewerMainXPL}
                        file={`${window.globalBase64String}`}
                        onLoadSuccess={handleLoadSuccess}
                        options={pdfOptions}
                        noData={<div className={stylesXplrr.loadingText}>Loading...</div>}
                        loading={<div className={stylesXplrr.loadingText}>Loading...</div>}
                        onLoadError={(error: any) => {setLoadingMessage("ERROR LOADING PDF")}}
                        error={(err: any) => (
                            <div className={stylesXplrr.errorPDFLoadSystemMsg}>
                                <div className={stylesXplrr.iconNoContent}>
                                    <MdErrorOutline size={26} />
                                </div>
                                <div>Error Loading PDF: `{err?.message}`</div>
                            </div>
                        )}
                    >
                        <div className={stylesXplrr.pdfDocContainer}>
                            <div key={`pdfmainid9_${manualResizeWidth}`} className={stylesXplrr.pdfMain} id="pdfmainid9" onScroll={handleScrollCallAdv}>
                                {renderedPages || []}
                                <div className={stylesXplrr.pdfFooter}></div>
                            </div>
                        </div>
                    </Document>
                </div>
            )}
            {/* rectangle select overlay (fixed position, updated via ref for performance) */}
            <div ref={rectOverlayRef} className={stylesXplrr.rectSelectOverlay} style={{ display: "none" }}/>
            {copiedToast && <div className={stylesXplrr.copiedToast}><div className={stylesXplrr.copiedToastBoldInfo}>Copied to clipboard:</div>{copiedToast}</div>}
        </div>
    );
};
export default XplrrBOXPDFZoom;
